import { DataPeriod } from '@prisma/client';
import { Button } from '@zond/ui';
import { cn } from '@zond/utils';
import { endOfMonth, endOfWeek, format, getISOWeek, startOfMonth, startOfWeek } from 'date-fns';
import { CalendarIcon, ChevronDown } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { useTranslation } from 'react-i18next';
import 'react-datepicker/dist/react-datepicker.css';
import './report-range-picker.css';
import { ToggleGroup, ToggleGroupItem } from '@zond/ui';

interface ReportRangePickerProps {
  selectedRange: DataPeriod;
  onRangeChange: (range: DataPeriod) => void;
  selectedDate: Date;
  onDateChange: (date: Date) => void;
}

const ranges: { labelKey: string; value: DataPeriod }[] = [
  { labelKey: 'range.daily', value: DataPeriod.Daily },
  { labelKey: 'range.weekly', value: DataPeriod.Weekly },
  { labelKey: 'range.monthly', value: DataPeriod.Monthly },
];

export const ReportRangePicker: React.FC<ReportRangePickerProps> = ({
  selectedRange,
  onRangeChange,
  selectedDate,
  onDateChange,
}) => {
  const { t } = useTranslation('reports');
  const [isOpen, setIsOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const formatDateRange = (date: Date) => {
    if (!date) return t('range.pickDate');

    switch (selectedRange) {
      case DataPeriod.Weekly: {
        const start = startOfWeek(date, { weekStartsOn: 1 });
        const end = endOfWeek(date, { weekStartsOn: 1 });
        return `${format(start, 'MMM d')} - ${format(end, 'MMM d, yyyy')}`;
      }
      case DataPeriod.Monthly: {
        const start = startOfMonth(date);
        const end = endOfMonth(date);
        return format(start, 'MMMM yyyy');
      }
      default:
        return format(date, 'MMM d, yyyy');
    }
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      if (selectedRange === DataPeriod.Weekly) {
        onDateChange(startOfWeek(date, { weekStartsOn: 1 }));
      } else {
        onDateChange(date);
      }
      setIsOpen(false);
    }
  };

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsOpen(!isOpen);
  };

  return (
    <div className="flex items-center gap-4">
      <div className="relative" ref={calendarRef}>
        <Button
          variant="outline"
          className={cn('justify-start text-left font-normal w-[240px]')}
          onClick={handleButtonClick}
        >
          <CalendarIcon className="mr-2 h-4 w-4 opacity-50" />
          {formatDateRange(selectedDate)}
          <ChevronDown className="ml-auto h-4 w-4 opacity-50" />
        </Button>

        {isOpen && (
          <div className="absolute top-[calc(100%+4px)] left-0 z-50">
            <DatePicker
              selected={selectedDate}
              onChange={handleDateChange}
              inline
              showWeekPicker={selectedRange === DataPeriod.Weekly}
              showMonthYearPicker={selectedRange === DataPeriod.Monthly}
              formatWeekNumber={getISOWeek}
              weekLabel={t('range.week')}
              calendarStartDay={1}
            />
          </div>
        )}
      </div>

      <ToggleGroup
        type="single"
        value={selectedRange}
        onValueChange={(value: string) => onRangeChange(value as DataPeriod)}
        className="h-9 bg-muted p-1 rounded-lg w-fit gap-0"
      >
        {ranges.map(({ labelKey, value }) => (
          <ToggleGroupItem
            key={value}
            value={value}
            className="h-7 w-[80px] text-sm font-medium flex items-center justify-center text-muted-foreground"
          >
            {t(labelKey)}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </div>
  );
};
